import { CommonModule } from '@angular/common';
import { Component, Input, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { KENDO_TOOLTIPS } from '@progress/kendo-angular-tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { DateTimeHelper } from '@pmis3/core';
import { ButtonSize, KENDO_BUTTONS } from '@progress/kendo-angular-buttons';
import { KENDO_DIALOGS } from '@progress/kendo-angular-dialog';

@Component({
    standalone: true,
    imports: [
        CommonModule,
        KENDO_BUTTONS,
        KENDO_TOOLTIPS,
        KENDO_DIALOGS,
    ],
    selector: 'application-screenshot',
    templateUrl: './application-screenshot.component.html',
})
export class ApplicationScreenshotComponent {
    @Input() componentClass = '';
    @Input() buttonClass = '';
    @Input() enableScreenshot = true;
    @Input() enableRecording = true;
    @Input() isMenuHeaderVisible = false;
    @Input() label: string = 'PMIS';
    @Input() dialogWidth = 1000;
    @Input() dialogClass!: string;
    @Input() buttonSize: ButtonSize = 'small';
    @Input() showTooltip = true;
    @Input() buttonFillMode = 'solid';

    @ViewChild('video') video!: ElementRef<HTMLVideoElement>;

    isSafari = false;
    isScreenshotLoading = false;
    isScreenshotDialogOpen = false;
    isScreenRecording = false;
    isImage = false;
    assetDataUrl = '';
    
    private _chunks: Blob[] = [];
    private _mediaRecorder?: MediaRecorder;
    private _videoBlob = '';
    private _stream?: MediaStream;
    private _videoDuration = 0;
    private videoExtension = 'mp4';

    constructor(
        private _sanitizer: DomSanitizer,
        private cdr: ChangeDetectorRef
    ) {
        if (!this.isSafari) this.videoExtension = 'webm';
        if (typeof MediaRecorder.isTypeSupported === 'function') {
            if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ||
                MediaRecorder.isTypeSupported('video/webm;codecs=vp8') ||
                MediaRecorder.isTypeSupported('video/webm')) {
                this.videoExtension = 'webm';
            }
        }
    }

    private async _startStreaming(): Promise<MediaStream> {
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({
                video: { mediaSource: "screen", width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } },
                audio: false,
                preferCurrentTab: true,
                surfaceSwitching: "include",
                systemAudio: "exclude"
            } as any);
            this._stream = stream;
            const videoTracks = stream.getVideoTracks();
            if (videoTracks.length > 0) {
                videoTracks[0].addEventListener('ended', () => this._stopStreaming());
            }
            return stream;
        } catch (error) {
            const fallbackStream = await navigator.mediaDevices.getDisplayMedia({ video: { width: 1920 }, audio: false });
            this._stream = fallbackStream;
            return fallbackStream;
        }
    }

    private _stopStreaming(): void {
        if (this._stream) {
            this._stream.getTracks().forEach(track => track.stop());
            this._stream = undefined;
        }
    }

    private _waitForVideoReady(video: HTMLVideoElement): Promise<void> {
        return new Promise((resolve, reject) => {
            video.addEventListener('canplay', () => resolve(), { once: true });
            video.addEventListener('error', (error) => reject(error), { once: true });
        });
    }

    private _createHiddenVideo(): HTMLVideoElement {
        const video = document.createElement('video');
        video.autoplay = true;
        video.muted = true;
        video.playsInline = true;
        video.style.position = 'fixed';
        video.style.left = '-9999px';
        return video;
    }

    async takeScreenshot(): Promise<void> {
        this.isImage = true;
        this.isScreenshotLoading = true;
        this.cdr.detectChanges();

        const video = this._createHiddenVideo();
        const canvas = document.createElement('canvas');
        document.body.appendChild(video);

        try {
            this._stream = await this._startStreaming();
            this.isScreenshotLoading = false;
            video.srcObject = this._stream;

            await this._waitForVideoReady(video);
            await video.play();

            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                this.assetDataUrl = canvas.toDataURL('image/png');
            }
            this.isScreenshotDialogOpen = true;
        } catch (error) {
            console.error('Screenshot error:', error);
        } finally {
            this._stopStreaming();
            document.body.removeChild(video);
            this.isScreenshotLoading = false;
        }
    }

    private _getSupportedMimeType(): string {
        const types = [
            'video/webm;codecs=vp9',
            'video/webm;codecs=vp8',
            'video/webm',
            'video/mp4'
        ];
        for (const type of types) {
            if (MediaRecorder.isTypeSupported(type)) return type;
        }
        return '';
    }

    private _processRecording(): void {
        if (this._chunks.length === 0) {
            console.error('No recording data available');
            return;
        }
        this.isImage = false;
        this._videoBlob = window.URL.createObjectURL(new Blob(this._chunks, { type: this._chunks[0].type }));
        this.isScreenshotDialogOpen = true;

        setTimeout(() => {
            if (this.video?.nativeElement) {
                this.video.nativeElement.src = this._videoBlob;
                this.video.nativeElement.load();
                this.video.nativeElement.onloadedmetadata = () => {
                    // Always start from the beginning
                    this.video.nativeElement.currentTime = 0;
                    this._videoDuration = Math.floor(this.video.nativeElement.duration);

                    // Optionally, play automatically
                    this.video.nativeElement.play().catch(() => {});
                };
            }
            this.cdr.detectChanges();
        }, 100);
    }

    async recordScreen(): Promise<void> {
        this.isImage = false;
        try {
            this._stream = await this._startStreaming();
            this.isScreenRecording = true;
            this._stream.addEventListener('inactive', () => this.stopRecording());
            const mimeType = this._getSupportedMimeType();
            this._mediaRecorder = new MediaRecorder(this._stream, mimeType ? { mimeType } : undefined);
            this._mediaRecorder.addEventListener('dataavailable', (event) => {
                if (event.data && event.data.size > 0) this._chunks.push(event.data);
            });
            this._mediaRecorder.addEventListener('stop', () => this._processRecording());
            this._mediaRecorder.addEventListener('error', (error) => {
                console.error('MediaRecorder error:', error);
                this.isScreenRecording = false;
                this._stopStreaming();
            });
            this._mediaRecorder.start(10);
        } catch (error) {
            this.isScreenRecording = false;
            console.error('Recording initialization failed:', error);
        }
    }

    stopRecording(): void {
        this.isScreenRecording = false;
        if (this._mediaRecorder && this._mediaRecorder.state !== 'inactive') {
            this._mediaRecorder.stop();
        }
        this._stopStreaming();
    }

    saveAsset(): void {
        const downloadLink = document.createElement('a');
        const timestamp = DateTimeHelper.prototype.GetDateTimeStringWithSecond(new Date());
        if (this.isImage && this.assetDataUrl) {
            downloadLink.href = this.assetDataUrl;
            downloadLink.download = `${this.label} Screenshot ${timestamp}.png`;
        } else if (!this.isImage && this._videoBlob) {
            downloadLink.href = this._videoBlob;
            downloadLink.download = `${this.label} Recording ${timestamp}${this._videoDuration ? ` #t=${this._videoDuration}` : ''}.${this.videoExtension}`;
        } else {
            console.error('No asset data available for download');
            return;
        }
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        this.closeScreenshotDialog();
    }

    closeScreenshotDialog(): void {
        if (!this.isImage && this._videoBlob) {
            window.URL.revokeObjectURL(this._videoBlob);
        }
        this.isImage = false;
        this.isScreenshotDialogOpen = false;
        this._chunks = [];
        this.assetDataUrl = '';
        this._videoBlob = '';
        this._videoDuration = 0;
        this._mediaRecorder = undefined;
    }
}